import {
  Badge,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import QB from "../../assets/logo/qb.svg";
import { motion } from "framer-motion";
import { SidebarReturn, Setting, SideCollapse } from "../../assets/icons";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { IHeader, INotificationSystemRef } from "../../types/header";
import { RestrictedComponent } from "../../components/RestrictedComponent";
import { usePermission } from "../../context/permission";
import { AccountCircleOutlined, CheckRounded, DarkModeOutlined, KeyboardArrowDown, LightModeOutlined, NotificationsNoneOutlined, ReceiptLongOutlined, RoomOutlined, SettingsBrightnessOutlined, LanguageOutlined, HelpOutlineOutlined, RocketLaunchOutlined } from "@mui/icons-material";

export const Header = ({
  onDrawerToggle,
  onDrawerCollapse,
  sidebarOpen,
  user,
  navigateRoutes,
  handleNotificationClick,
  themeMode = "light",
  onToggleTheme,
  themePreference,
  onSelectThemePreference,
  languageSwitcher,
  myAccount,
  onTakeTour,
}: IHeader) => {
  const isDark = themeMode === "dark";
  const { hasPermission } = usePermission();
  const hasProfilePermission = hasPermission({
    module_name: "User",
    permission: "View",
  });
  const navigate = useNavigate();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const notificationRef = useRef<INotificationSystemRef>(null);
  const notificationButtonRef = useRef<HTMLButtonElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [themeAnchor, setThemeAnchor] = useState<null | HTMLElement>(null);
  const [langAnchor, setLangAnchor] = useState<null | HTMLElement>(null);
  const { unReadNotification } = useSelector(
    (state: any) => state.notification || { unReadNotification: [] }
  );
  const unreadCount = unReadNotification?.length || 0;
  const isMobile = windowWidth < 768;

  const { t } = useTranslation("common");
  const finalMyAccount = myAccount ?? t("common.myAccount");
  const activeLangOption = languageSwitcher?.options?.find(
    (o) => o.code === languageSwitcher.currentLanguage
  ) ?? languageSwitcher?.options?.[0];

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleMenuOpen = () => {
    if (user?.admin && navigateRoutes.SETTING) {
      navigate(navigateRoutes.SETTING);
      return;
    }

    if (hasProfilePermission) {
      navigate(navigateRoutes.PROFILE);
    } else if (navigateRoutes.CHANGE_PASSWORD) {
      navigate(navigateRoutes.CHANGE_PASSWORD);
    }
  };

  const handleProfileClick = (event: React.MouseEvent<HTMLElement>) => {
    if (user?.admin) {
      if (navigateRoutes.PROFILE) {
        navigate(navigateRoutes.PROFILE);
      }
      return;
    }
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchor(null);
  };

  const goToAccount = () => {
    if (hasProfilePermission) {
      navigate(navigateRoutes.PROFILE);
    } else if (navigateRoutes.CHANGE_PASSWORD) {
      navigate(navigateRoutes.CHANGE_PASSWORD);
    }
    handleProfileClose();
  };

  const goToBilling = () => {
    if (navigateRoutes.PLANS) {
      navigate(navigateRoutes.PLANS);
    }
    handleProfileClose();
  };

  const goToHelpAndSupport = () => {
    if (navigateRoutes.HELP_AND_SUPPORT) {
      navigate(navigateRoutes.HELP_AND_SUPPORT);
    }
    handleProfileClose();
  };

  return (
    <>
      <motion.div>
        <Box>
          <Toolbar className="h-11.5 flex items-center gap-2 pl-0!">
            {/* Left */}
            <Box className="flex items-center gap-2">
              {isMobile && (
                <IconButton onClick={onDrawerToggle}>
                  <SidebarReturn />
                </IconButton>
              )}

              {!isMobile && (
                <IconButton onClick={onDrawerCollapse} className="p-0!">
                  {sidebarOpen ? <SideCollapse /> : <SidebarReturn />}
                </IconButton>
              )}

              {isMobile && (
                <img
                  src={QB}
                  alt="Logo"
                  className="w-7 h-7 sm:w-8 sm:h-8 object-contain"
                />
              )}

              <Typography className="block md:hidden font-bold text-sm sm:text-base">
                {user?.admin ? "Admin" : ` ${t("common.salesBot")}`}
              </Typography>
            </Box>

            <Box className="grow" />

            {/* Right */}
            <Box className="flex items-center gap-1 sm:gap-2">
              <div className="flex items-center gap-2 md:gap-3 shrink-0">
                {user?.tenant?.tenant_timezone && (
                  <Tooltip title={`Your timezone is set to ${user?.tenant?.tenant_timezone}. All dates and times are displayed accordingly.`}>
                    <Chip
                      className="timezone-chip"
                      icon={<RoomOutlined style={{ opacity: 0.7, fontSize: "14px" }} className="timezone-chip-icon" />}
                      label={user?.tenant?.tenant_timezone}
                      size="small"
                      sx={(theme) => ({
                        height: 24,
                        fontSize: "11px",
                        fontWeight: 600,
                        borderRadius: "6px",
                        color: theme.palette.mode === "dark" ? "#f3f4f6" : "rgba(31, 41, 55, 1)",
                        background: theme.palette.mode === "dark" ? "#1f2937" : "white",
                        border: "1.5px solid transparent",
                        backgroundImage:
                          theme.palette.mode === "dark"
                            ? "linear-gradient(#1f2937, #1f2937), linear-gradient(91.11deg, #9673e9 0.3%, #c558e5 29.11%, #ec9c75 57.17%, #ec7b5c 76.66%, #e9ae89 98.87%)"
                            : "linear-gradient(white, white), linear-gradient(91.11deg, #9673e9 0.3%, #c558e5 29.11%, #ec9c75 57.17%, #ec7b5c 76.66%, #e9ae89 98.87%)",
                        backgroundOrigin: "border-box",
                        backgroundClip: "padding-box, border-box",
                        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                        display: { xs: "none", sm: "flex" },
                        "& .MuiChip-label": {
                          px: 1,
                        },
                        "& .MuiChip-icon": {
                          ml: 0.5,
                          mr: -0.5,
                          color: theme.palette.mode === "dark" ? "#f3f4f6" : "black",
                        },
                      })}
                    />
                  </Tooltip>
                )}
                {onSelectThemePreference ? (
                  <>
                    <Tooltip
                      title={
                        themePreference === "system"
                          ? "Theme: System"
                          : themePreference === "dark"
                            ? "Theme: Dark"
                            : "Theme: Light"
                      }
                    >
                      <IconButton
                        onClick={(e) => setThemeAnchor(e.currentTarget)}
                        aria-label="theme menu"
                      >
                        {themePreference === "system" ? (
                          <SettingsBrightnessOutlined
                            style={{ width: "1.182rem", height: "1.182rem" }}
                          />
                        ) : themePreference === "dark" ? (
                          <DarkModeOutlined
                            style={{ width: "1.182rem", height: "1.182rem" }}
                          />
                        ) : (
                          <LightModeOutlined
                            style={{ width: "1.182rem", height: "1.182rem" }}
                          />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={themeAnchor}
                      open={Boolean(themeAnchor)}
                      onClose={() => setThemeAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      slotProps={{
                        paper: {
                          sx: (theme) => ({
                            mt: 1,
                            minWidth: 180,
                            borderRadius: "10px",
                            border:
                              theme.palette.mode === "dark"
                                ? "1px solid #323232"
                                : "1px solid #e5e7eb",
                            boxShadow:
                              theme.palette.mode === "dark"
                                ? "0 8px 24px rgba(0,0,0,0.5)"
                                : "0 8px 24px rgba(0,0,0,0.12)",
                            overflow: "hidden",
                          }),
                        },
                      }}
                      MenuListProps={{ sx: { py: 0.5 } }}
                    >
                      {(
                        [
                          {
                            value: "system" as const,
                            label: "System",
                            Icon: SettingsBrightnessOutlined,
                          },
                          {
                            value: "light" as const,
                            label: "Light",
                            Icon: LightModeOutlined,
                          },
                          {
                            value: "dark" as const,
                            label: "Dark",
                            Icon: DarkModeOutlined,
                          },
                        ]
                      ).map((opt) => {
                        const selected = themePreference === opt.value;
                        return (
                          <MenuItem
                            key={opt.value}
                            selected={selected}
                            onClick={() => {
                              onSelectThemePreference(opt.value);
                              setThemeAnchor(null);
                            }}
                            sx={{
                              mx: 0.5,
                              my: 0.25,
                              borderRadius: "6px",
                              px: 1.25,
                              py: 0.75,
                              fontSize: "0.875rem",
                              fontWeight: selected ? 600 : 500,
                            }}
                          >
                            <ListItemIcon
                              sx={(theme) => ({
                                minWidth: 28,
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#d1d5db"
                                    : "#4B5563",
                              })}
                            >
                              <opt.Icon sx={{ fontSize: 18 }} />
                            </ListItemIcon>
                            <ListItemText
                              primaryTypographyProps={{ fontSize: "0.875rem" }}
                            >
                              {opt.label}
                            </ListItemText>
                            {selected && (
                              <CheckRounded
                                sx={(theme) => ({
                                  fontSize: 16,
                                  ml: 1,
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#f3f4f6"
                                      : "#111827",
                                })}
                              />
                            )}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                ) : onToggleTheme ? (
                  <Tooltip title={isDark ? "Switch to Light Mode" : "Switch to Dark Mode"}>
                    <IconButton onClick={onToggleTheme} aria-label="toggle theme">
                      {isDark ? (
                        <LightModeOutlined
                          style={{ width: "1.182rem", height: "1.182rem" }}
                        />
                      ) : (
                        <DarkModeOutlined
                          style={{ width: "1.182rem", height: "1.182rem" }}
                        />
                      )}
                    </IconButton>
                  </Tooltip>
                ) : null}
                <RestrictedComponent permission={{
                  module_name: "Notification",
                  permission: "View"
                }}>
                  <IconButton onClick={(e) => handleNotificationClick?.(e)} data-notification-button>
                    <Badge badgeContent={unreadCount} color="error">
                      <NotificationsNoneOutlined
                        style={{
                          width: "1.182rem",
                          height: "1.182rem",
                        }}
                      />
                    </Badge>
                  </IconButton>
                </RestrictedComponent>

                <IconButton onClick={handleMenuOpen}>
                  <Setting />
                </IconButton>

                {/* ── Language switcher ── */}
                {languageSwitcher && activeLangOption && (
                  <>
                    <Tooltip title={activeLangOption.label}>
                      <IconButton
                        size="small"
                        onClick={(e) => setLangAnchor(e.currentTarget)}
                        sx={{ gap: 0.5, px: 1 }}
                      >
                        <LanguageOutlined sx={{ width: "1.1rem", height: "1.1rem" }} />
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          lineHeight={1}
                          sx={{ letterSpacing: "0.05em" }}
                        >
                          {activeLangOption.code.toUpperCase()}
                        </Typography>
                      </IconButton>
                    </Tooltip>

                    <Menu
                      anchorEl={langAnchor}
                      open={Boolean(langAnchor)}
                      onClose={() => setLangAnchor(null)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      transformOrigin={{ vertical: "top", horizontal: "right" }}
                      disableAutoFocusItem
                      slotProps={{
                        paper: {
                          sx: (theme) => ({
                            mt: 1,
                            minWidth: 200,
                            px: 1,
                            py: 0.5,
                            borderRadius: "0.75rem",
                            border:
                              theme.palette.mode === "dark"
                                ? "1px solid #323232"
                                : "1px solid #e5e7eb",
                            boxShadow:
                              theme.palette.mode === "dark"
                                ? "0 8px 24px rgba(0,0,0,0.5)"
                                : "0 8px 24px rgba(0,0,0,0.12)",
                            overflow: "hidden",
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? theme.palette.background.paper
                                : undefined,
                          }),
                        },
                      }}
                      MenuListProps={{ sx: { py: 0.5 } }}
                    >
                      {languageSwitcher.options?.map((lang) => {
                        const selected =
                          lang.code === languageSwitcher.currentLanguage;
                        return (
                          <MenuItem
                            key={lang.code}
                            selected={selected}
                            onClick={() => {
                              languageSwitcher.onLanguageChange(lang.code);
                              setLangAnchor(null);
                            }}
                            sx={(theme) => ({
                              borderRadius: "0.5rem",
                              py: 1,
                              mx: 0.5,
                              my: 0.25,
                              "&.Mui-selected": {
                                backgroundColor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(129, 140, 248, 0.22)"
                                    : theme.palette.action.selected,
                                "&:hover": {
                                  backgroundColor:
                                    theme.palette.mode === "dark"
                                      ? "rgba(129, 140, 248, 0.3)"
                                      : undefined,
                                },
                              },
                            })}
                          >
                            <ListItemText
                              primary={lang.nativeLabel}
                              secondary={lang.label}
                              primaryTypographyProps={{
                                fontWeight: selected ? 600 : 500,
                                fontSize: "0.875rem",
                                sx: (theme) => ({
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#f9fafb"
                                      : theme.palette.text.primary,
                                }),
                              }}
                              secondaryTypographyProps={{
                                fontSize: "0.75rem",
                                sx: (theme) => ({
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#9ca3af"
                                      : theme.palette.text.secondary,
                                }),
                              }}
                            />
                            {selected && (
                              <CheckRounded
                                sx={(theme) => ({
                                  fontSize: 18,
                                  ml: 1,
                                  flexShrink: 0,
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#e5e7eb"
                                      : "#111827",
                                })}
                              />
                            )}
                          </MenuItem>
                        );
                      })}
                    </Menu>
                  </>
                )}

                <div
                  onClick={handleProfileClick}
                  className="hidden md:flex items-center gap-3 px-2.5 py-2.5 w-[14.438rem] h-11.5 rounded-lg bg-header-profile cursor-pointer transition-all hover:shadow-medium"
                >
                  <img
                    src={QB}
                    alt="Profile"
                    className="w-10 h-10 rounded-lg object-cover border-2 border-white shadow-medium shrink-0"
                  />

                  <div className="flex-1 min-w-0">
                    <p
                      className="text-sm font-semibold text-header-foreground-hover truncate"
                      style={{
                        fontFamily: "Segoe UI",
                        lineHeight: "1.125rem",
                      }}
                    >

                      {user?.admin?.first_name ? user?.admin?.first_name : user?.user?.first_name}

                    </p>
                    <p
                      className="text-xs text-header-foreground truncate mt-0.5"
                      style={{
                        fontFamily: "Segoe UI",
                        lineHeight: "1rem",
                      }}
                    >
                      {user?.admin?.email ? user?.admin?.email : user?.user?.email}
                    </p>
                  </div>
                  {user?.user && (
                    <KeyboardArrowDown
                      className="text-header-foreground shrink-0 transition-transform"
                      style={{
                        width: "1.25rem",
                        height: "1.25rem",
                      }}
                    />)}
                </div>

                <div className="md:hidden flex items-center cursor-pointer">
                  <img
                    src={QB}
                    alt="Profile"
                    className="w-9 h-9 rounded-lg object-cover border-2 border-white shadow-medium"
                  />
                </div>
              </div>
            </Box>
          </Toolbar>
        </Box>
      </motion.div>
      <Menu
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={handleProfileClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        disableAutoFocusItem
        PaperProps={{
          sx: {
            borderRadius: "0.75rem",
            minWidth: 220,
            px: 1,
          },
        }}
      >
        <MenuItem
          onClick={goToAccount}
          sx={{ borderRadius: "0.75rem", py: 1.25 }}
        >
          <ListItemIcon>
            <AccountCircleOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText primary={finalMyAccount} />
        </MenuItem>
        <RestrictedComponent permission={{
          module_name: "Subscription",
          permission: "View"
        }}>
          <MenuItem
            onClick={goToBilling}
            sx={{ borderRadius: "0.75rem", py: 1.25 }}
          >
            <ListItemIcon>
              <ReceiptLongOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("common.billingAndPlans")} />
          </MenuItem>
        </RestrictedComponent>
        {
          navigateRoutes.HELP_AND_SUPPORT && (
            <MenuItem
              onClick={goToHelpAndSupport}
              sx={{ borderRadius: "0.75rem", py: 1.25 }}
            >
              <ListItemIcon>
                <HelpOutlineOutlined fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t("common.helpAndSupport", "Help & Support")} />
            </MenuItem>
          )}
        {onTakeTour && (
          <MenuItem
            id="header-take-a-tour-btn"
            onClick={() => {
              handleProfileClose();
              onTakeTour();
            }}
            sx={{ borderRadius: "0.75rem", py: 1.25 }}
          >
            <ListItemIcon>
              <RocketLaunchOutlined fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t("common.takeATour")} />
          </MenuItem>
        )}
      </Menu>
    </>
  );
};
